import { blobMediaUrl } from "@/lib/blob-url"
import { PRODUCT_IDS } from "@/lib/booking"
import type { Availability } from "@/lib/availability"
export interface TestData {
  slug: string
  price: number
  /**
   * The in-membership (member) price in CHF when it's lower than the one-off
   * `price`. Surfaced on the detail-page hero in the membership markets only
   * (struck one-off + member price). CHF-only, like `price`. Numeric source
   * mirrors `MEMBER_PRICING_ITEMS` in membership-client.tsx — keep the two in
   * sync.
   */
  memberPrice?: number
  /**
   * This test is the membership's entry product (the advanced blood test), so
   * in the membership markets the hero price leads with the annual
   * membership price (`MEMBERSHIP_ANNUAL_CHF`/year) + a "membership" pill and
   * an "also available as a one-off" sub-line, instead of a per-test discount.
   */
  membershipEntry?: boolean
  /**
   * AHD-13150 — imaging add-ons with a single price (booked on top of an MRI,
   * no cheaper variant): the hero drops "From" and, in the membership
   * markets, shows both prices in the /pricing add-on format
   * ("CHF {one-off} · CHF {member} for members") instead of leading with the member
   * rate alone. Opt-in so the blood panels keep their member-first hero.
   */
  heroBothPrices?: boolean
  priceSuffix?: string
  duration: string
  heroImage: string
  trustBadgeKeys: string[]
  /** Which pricing cards to show on the test detail page */
  pricingType: "mri" | "blood"
  /**
   * AHD-8411 — who this test is for, when it's a gendered product (hormone
   * test for women/men). Drives the testimonials query on the detail page:
   * reviews whose CMS `authorGender` is the *opposite* gender are excluded;
   * unlabeled reviews always show. Omit for non-gendered tests.
   */
  audience?: "female" | "male"
  quickFacts: {
    label: string
    /**
     * Either a literal display string ("60") or one of:
     * - a translation key starting with `qf` (resolved against the `tests`
     *   namespace at render time);
     * - the sentinel `"cms:biomarker-count"` — replaced at render time in
     *   `tests/[slug]/page.tsx` with the live count of biomarkers in the
     *   package mapped from the test slug via
     *   `TEST_SLUG_TO_BIOMARKER_PACKAGE` (see lib/categories.ts).
     */
    value: string
    unit?: string
    detail: string
    icon:
      | "clock"
      | "no-radiation"
      | "body-scan"
      | "checklist"
      | "person"
      | "droplet"
      | "imaging"
  }[]
  questions: {
    photo: string
    biomarkerValue: string
    biomarkerValueKey?: string
    biomarkerRange?: string
    biomarkerStatus: "normal" | "outOfRange" | "observation" | "negative"
    /** Optional blog (insights) slug for a "Learn more" link on the card. */
    articleSlug?: string
    /** CSS object-position for the circular thumbnail (defaults to center). */
    photoObjectPosition?: string
    /** Keep this reading off the hero result cards (it still renders as a
     *  question card) — e.g. prostate "No findings" beside a PI-RADS 2 score,
     *  which is a per-lesion category and would contradict it (AHD-13183). */
    hideInHero?: boolean
  }[]
  processSteps: {
    color: string
    ctaHref?: string
  }[]
  diseaseCategories: {
    category: string
    diseases: { name: string; slug?: string }[]
  }[]
  /** CMS package IDs to filter biomarkers (empty/omit = no biomarker section) */
  biomarkerPackages?: string[]
  /** Images for the "three simple steps" section [step1, step2, step3] */
  stepsImages?: [string, string, string]
  /** Translation key prefix for steps (e.g., "mriAddon" → mriAddonStep1Heading) */
  stepsKeyPrefix?: string
  /**
   * When true, the steps section renders a 4-card flow with a dedicated
   * "Pre-consultation" card between Book and Scan. Tests with a physician
   * pre-consult as part of the product (CAC, mamma-mri) set this so the
   * page honestly reflects that booking is not zero-effort.
   *
   * Pulls translation keys `<stepsKeyPrefix>PreConsultHeading` /
   * `<stepsKeyPrefix>PreConsultDesc` and an additional image from
   * `preConsultImage`.
   */
  hasPreConsultation?: boolean
  /** Image for the pre-consultation card (only when hasPreConsultation). */
  preConsultImage?: string
  /**
   * When set, "Book" CTAs on this test's detail page deeplink into the
   * booking flow with this product pre-selected. Omit for tests where the
   * default booking entry (no pre-selection) is the right destination —
   * e.g. ambiguous packages (full-body MRI = Core/Advanced/Pro) or
   * imaging/blood add-ons that don't map to a standalone booking product.
   */
  bookingProductId?: keyof typeof PRODUCT_IDS
  /** Per-country-locale availability. Absent = available everywhere. */
  availability?: Availability
  /**
   * Curated "related reading" articles, by CMS blog-post doc `id` (stable and
   * locale-invariant — blog slugs are localized, so a slug can't be keyed on).
   * Resolved against the already-loaded, country-filtered blog index at render
   * time; a hidden/absent post simply drops out. Order is preserved.
   */
  relatedArticleIds?: string[]
}

/** Slug → translation key prefix for per-test content in the tests namespace */
export const TEST_KEY_PREFIX: Record<string, string> = {
  "full-body-mri": "mri",
  "advanced-blood-test": "blood",
  "calcium-score": "calcium",
  "body-composition": "bodyComp",
  "dexa-scan": "dexa",
  "gender-health": "genderHealth",
  "prostate-mri": "prostateMri",
  // Slug renamed ovaries-mri -> pelvic-mri (SEO + medical accuracy: the scan
  // covers uterus + endometrium + ovaries). Internal key prefix stays
  // `ovariesMri` to avoid churning every translation key.
  "pelvic-mri": "ovariesMri",
  "brain-volumetry": "brain",
  "mamma-mri": "mamma",
  "essential-b-vitamins-test": "bVitamins",
  "omega-blood-test": "omega",
  "hormone-blood-test-women": "hormoneWomen",
  "hormone-blood-test-men": "hormoneMen",
  "advanced-blood-panel": "bloodPanel",
  "prevention-starter": "preventionStarter",
}

/**
 * Tests whose disease/biomarker explorer needs its own intro on the health-risks
 * tab. Without an entry the explorer falls back to `tests.diseasesSubheading`,
 * which describes the full-body MRI, so any non-MRI imaging test that shows the
 * health-risks tab (DEXA, AHD-13136) must be listed here. Keys live in the
 * `tests` namespace of every message file.
 */
const DISEASES_SUBHEADING_KEY: Record<string, string> = {
  "dexa-scan": "dexaDiseasesSubheading",
  "prostate-mri": "prostateMriDiseasesSubheading",
  "pelvic-mri": "ovariesMriDiseasesSubheading",
}

export function diseasesSubheadingKeyFor(slug: string): string | undefined {
  return DISEASES_SUBHEADING_KEY[slug]
}

/**
 * Package chip the explorer preselects on a test page. The full-body MRI page
 * opens on Core so the counts match the "from" price above it (AHD-13182).
 * Every other page keeps the unfiltered "All" view.
 */
const EXPLORER_DEFAULT_PACKAGE: Record<string, string> = {
  "full-body-mri": "core",
}

export function explorerDefaultPackageFor(slug: string): string | undefined {
  return EXPLORER_DEFAULT_PACKAGE[slug]
}

export const DEXA_TEST_SLUG = "dexa-scan"
export const GENDER_HEALTH_TEST_SLUG = "gender-health"
export const PROSTATE_MRI_TEST_SLUG = "prostate-mri"
export const OVARIES_MRI_TEST_SLUG = "pelvic-mri"

/**
 * Baseline annual Ahead membership price (CHF, 1×/year
 * tier). Drives the "CHF {n}/year" headline on `membershipEntry` test pages.
 * Mirrors the baseline in `FREQUENCY_OPTIONS` (membership-client.tsx).
 */
export const MEMBERSHIP_ANNUAL_CHF = 299
// `new-tests-pages` originally gated DEXA, gender-health, and the
// prostate/pelvic split. All the test detail pages are now ungated: DEXA,
// prostate-mri, and pelvic-mri are fully public. gender-health is the combined
// page the two split scans replaced — it stays routable but hidden from listing
// surfaces (see TEST_LISTING_HIDDEN_SLUGS below), so no test slug is
// flag-gated any more. The flag still gates non-test surfaces (what-we-detect
// disease content, go/insurance add-on cards), so the constant stays.
export const NEW_TESTS_PAGES_RELEASE_FLAG = "new-tests-pages"
export const NEW_TESTS_PAGE_SLUGS = [] as const

/** Legacy slugs kept routable at /tests/{slug} but hidden from listing surfaces
 *  (sitemap, search index, /tests listing). gender-health was superseded by
 *  prostate-mri + pelvic-mri but stays reachable so old links don't 404. */
export const TEST_LISTING_HIDDEN_SLUGS = [
  "advanced-blood-panel",
  GENDER_HEALTH_TEST_SLUG,
] as const

export const IMAGING_ADDONS = [
  "body-composition",
  DEXA_TEST_SLUG,
  GENDER_HEALTH_TEST_SLUG,
  PROSTATE_MRI_TEST_SLUG,
  OVARIES_MRI_TEST_SLUG,
  "brain-volumetry",
  "mamma-mri",
  "calcium-score",
]
export const BLOOD_ADDONS = [
  "omega-blood-test",
  "essential-b-vitamins-test",
  "hormone-blood-test-women",
  "hormone-blood-test-men",
  "lpa",
  "cortisol-blood-test",
  "psa-blood-test",
]
const MAIN_PRODUCTS = ["full-body-mri", "advanced-blood-test"]

/**
 * Whether a test slug is an imaging product — full-body MRI itself or any of
 * its imaging add-ons (DEXA, mamma-MRI, calcium score, …). These map to the
 * `mri` product category (everything else is `blood`). Single source of truth
 * for the `mri` vs `blood` split, matching the BookingCta `category` logic on
 * the test-detail page.
 */
export function isImagingProductSlug(slug: string): boolean {
  return slug === "full-body-mri" || IMAGING_ADDONS.includes(slug)
}

/** Legacy test slugs (old CMS / Framer links) → current slug. */
export const TEST_SLUG_ALIASES: Record<string, string> = {
  "brain-analysis": "brain-volumetry",
}

/** Get recommended test slugs for the "made for each other" cross-sell section */
export function getCrossSellSlugs(currentSlug: string): string[] {
  if (currentSlug === "full-body-mri")
    return [...IMAGING_ADDONS, ...BLOOD_ADDONS]
  if (
    currentSlug === "advanced-blood-test" ||
    currentSlug === "advanced-blood-panel"
  )
    return BLOOD_ADDONS
  // DEXA cross-sell: skip body-composition (DEXA already reports body-comp,
  // showing both side-by-side confuses readers) and brain-volumetry (no
  // editorial overlap). Keep MRI, hormone panels, and advanced blood test.
  if (currentSlug === DEXA_TEST_SLUG)
    return [
      "full-body-mri",
      "advanced-blood-test",
      "hormone-blood-test-women",
      "hormone-blood-test-men",
      "calcium-score",
      "mamma-mri",
    ]
  // Prostate + ovaries MRI show an "Additional imaging add-ons" carousel: the
  // calcium-score imaging set, minus the gender-health products themselves (the
  // combined gender-health card and both gender-specific MRI scans) so the
  // section never cross-sells the page you're on or its sibling.
  if (
    currentSlug === PROSTATE_MRI_TEST_SLUG ||
    currentSlug === OVARIES_MRI_TEST_SLUG
  )
    return getCrossSellSlugs("calcium-score").filter(
      (slug) =>
        slug !== GENDER_HEALTH_TEST_SLUG &&
        slug !== PROSTATE_MRI_TEST_SLUG &&
        slug !== OVARIES_MRI_TEST_SLUG
    )
  if (IMAGING_ADDONS.includes(currentSlug))
    return [
      ...MAIN_PRODUCTS,
      ...IMAGING_ADDONS.filter((s) => s !== currentSlug),
      ...BLOOD_ADDONS,
    ]
  if (BLOOD_ADDONS.includes(currentSlug))
    return [...MAIN_PRODUCTS, ...BLOOD_ADDONS.filter((s) => s !== currentSlug)]
  return [...MAIN_PRODUCTS, ...IMAGING_ADDONS, ...BLOOD_ADDONS].filter(
    (s) => s !== currentSlug
  )
}

export const TESTS: TestData[] = [
  {
    slug: "full-body-mri",
    // Germany sells the blood check-up and its add-ons, nothing else, so every
    // imaging product is unavailable there: the detail page 404s (the
    // `isAvailable` gate in tests/v/[code]/[slug]/page.tsx), the listing drops
    // the card and app/sitemap.ts stops emitting the URL. Restore when
    // DE_EXPANSION opens MRI booking.
    availability: { "de-de": false, "de-en": false },
    price: 1990,
    duration: "30–55min",
    heroImage: blobMediaUrl("/images/mri/hero.jpeg"),
    trustBadgeKeys: [
      "trustBadgeQuickSafe",
      "trustBadgeDoctorReviewed",
      "trustBadgeActionableReport",
    ],
    pricingType: "mri",
    // Related reading (CMS blog-post ids): 13 Vorsorge & Krankenkasse (cost),
    // 11 Ganzkörper-MRI Leitfaden, 18 Ablauf & Ergebnisse, 6 MRI vs CT.
    relatedArticleIds: ["13", "11", "18", "6"],
    biomarkerPackages: ["core", "advanced", "pro"],
    stepsImages: [
      blobMediaUrl("/images/steps/mri/01-book.jpg"),
      blobMediaUrl("/images/steps/mri/02-fullbody-scan.jpg"),
      blobMediaUrl("/images/steps/blood/03-results.jpg"),
    ],
    quickFacts: [
      {
        label: "qfTimeLabel",
        value: "qfTimeValue",
        unit: "qfTimeUnit",
        detail: "qfTimeDetail",
        icon: "clock",
      },
      {
        label: "qfRadiationLabel",
        value: "",
        detail: "qfRadiationDetail",
        icon: "no-radiation",
      },
      {
        label: "qfAreasLabel",
        // Matches the "12 organ systems" used everywhere else on the site
        // (statValue3, htc_* keys, report page). AHD-13182.
        value: "12",
        detail: "qfAreasDetail",
        icon: "body-scan",
      },
      {
        label: "",
        value: "",
        detail: "qfSummaryDetail",
        icon: "clock",
      },
      {
        label: "qfPrepLabel",
        value: "",
        detail: "qfPrepDetail",
        icon: "checklist",
      },
      {
        label: "qfForWhomLabel",
        value: "18-80",
        detail: "qfForWhomDetail",
        icon: "person",
      },
    ],
    questions: [
      {
        photo: blobMediaUrl("/images/questions/question-aging.png"),
        biomarkerValue: "2.2L",
        biomarkerRange: "1.7 - 7.5",
        biomarkerStatus: "normal",
        articleSlug:
          "visceral-fat-the-hidden-health-risks-of-belly-fat-and-how-to-reduce-it",
      },
      {
        photo: blobMediaUrl("/images/questions/question-liver-cancer.png"),
        biomarkerValue: "No findings",
        biomarkerValueKey: "noFindingSingular",
        biomarkerStatus: "negative",
        articleSlug: "cancer-survival-rates-statistics",
        // Faces sit in the upper third — anchor to top so they're not cropped.
        photoObjectPosition: "top",
        // A "Liver cancer · No findings · Negative" hero chip reads as a
        // cancer all-clear the scan can't give; the question card stays
        // (AHD-13147).
        hideInHero: true,
      },
      {
        photo: blobMediaUrl("/images/questions/question-bloating.png"),
        biomarkerValue: "3.9%",
        biomarkerRange: "< 6",
        biomarkerStatus: "normal",
        articleSlug: "fatty-liver-disease-guide",
      },
      {
        photo: blobMediaUrl("/images/questions/question-back-pain.png"),
        biomarkerValue: "17.1%",
        biomarkerRange: "16 - 34",
        biomarkerStatus: "normal",
        articleSlug: "lower-back-pain-what-your-back-muscles-reveal",
      },
    ],
    processSteps: [
      { color: "#6E8CD2", ctaHref: "/locations" },
      { color: "#9BA528", ctaHref: "/free-call" },
      { color: "#7F4685", ctaHref: "/report" },
      { color: "#ED9062" },
    ],
    diseaseCategories: [
      {
        category: "gut",
        diseases: [
          { name: "Adenomyomatosis" },
          { name: "Agenesis of the liver" },
          { name: "Angiomyxoma of the liver" },
          { name: "Appendicitis" },
          { name: "Beaver tail liver" },
          { name: "Bile duct stricture" },
          { name: "Biliary cancer" },
          { name: "Biliary cystadema" },
          { name: "Biliary hamartoma of the liver" },
          { name: "Bowel carcinoid" },
          { name: "Bowel lymphoma" },
          { name: "Cholecystitis" },
        ],
      },
      {
        category: "cardiovascular",
        diseases: [
          { name: "Aortic aneurysm" },
          { name: "Aortic dissection" },
          { name: "Pericardial effusion" },
          { name: "Cardiac mass" },
          { name: "Cardiomegaly" },
        ],
      },
      {
        category: "immune",
        diseases: [
          { name: "Lymphadenopathy" },
          { name: "Splenomegaly" },
          { name: "Splenic infarction" },
        ],
      },
      {
        category: "bone-muscle",
        diseases: [
          { name: "Disc herniation" },
          { name: "Disc protrusion" },
          { name: "Spinal stenosis" },
          { name: "Scoliosis" },
          { name: "Vertebral fracture" },
        ],
      },
      {
        category: "brain",
        diseases: [
          { name: "Brain aneurysm" },
          { name: "Brain tumor" },
          { name: "White matter lesions" },
          { name: "Cerebral atrophy" },
        ],
      },
      {
        category: "women",
        diseases: [
          { name: "Ovarian cyst" },
          { name: "Uterine fibroids" },
          { name: "Endometriosis" },
        ],
      },
    ],
  },
  {
    slug: "advanced-blood-test",
    price: 299,
    membershipEntry: true, // gateway product
    duration: "15min",
    heroImage: blobMediaUrl("/images/homepage/service-blood.jpeg"),
    trustBadgeKeys: [
      "trustBadgeQuickSafe",
      "trustBadgeDoctorReviewed",
      "trustBadgeActionableReport",
    ],
    pricingType: "blood",
    bookingProductId: "bloodTest",
    // `blood` is the core panel; the `addon-*` entries surface optional
    // blood add-on biomarkers (B-vitamins, Omega-3/6, Hormones, trace
    // elements, heavy metals, coeliac) inside the explorer so users can see
    // what's available alongside the base test. Add-on biomarkers wear the
    // small "Add-on" pill in the row.
    biomarkerPackages: [
      "blood",
      "addon-bvitamins",
      "addon-hormones-f",
      "addon-hormones-m",
      "addon-omega",
      "addon-trace-elements",
      "addon-heavy-metals",
      "addon-coeliac",
      "addon-autoimmune",
    ],
    stepsImages: [
      blobMediaUrl("/images/immunity/immunity-step-book-2.jpg"),
      blobMediaUrl("/images/immunity/immunity-step-blood-draw.jpg"),
      blobMediaUrl("/images/mri/walkthrough/step-report-v2.png"),
    ],
    stepsKeyPrefix: "blood",
    quickFacts: [
      {
        label: "qfBloodTimeLabel",
        value: "qfBloodTimeValue",
        unit: "qfBloodTimeUnit",
        detail: "qfBloodTimeDetail",
        icon: "clock",
      },
      {
        label: "qfBloodAreasLabel",
        value: "",
        detail: "qfBloodAreasDetail",
        icon: "droplet",
      },
      {
        label: "qfBloodMarkersLabel",
        value: "cms:biomarker-count",
        detail: "qfBloodMarkersDetail",
        icon: "body-scan",
      },
      {
        label: "",
        value: "",
        detail: "qfBloodSummaryDetail",
        icon: "clock",
      },
      {
        label: "qfBloodPrepLabel",
        value: "",
        detail: "qfBloodPrepDetail",
        icon: "checklist",
      },
      {
        label: "qfBloodForWhomLabel",
        value: "18-80",
        detail: "qfBloodForWhomDetail",
        icon: "person",
      },
    ],
    questions: [
      {
        photo: blobMediaUrl("/images/questions/blood-dizzy.png"),
        biomarkerValue: "165 µg/L",
        biomarkerRange: "30-350",
        biomarkerStatus: "normal",
        articleSlug: "iron-deficiency-what-a-full-iron-panel-can-tell-you",
      },
      {
        photo: blobMediaUrl("/images/questions/blood-colds.jpeg"),
        // White blood cells, not a unitless lymphocyte figure (AHD-13587):
        // 3.6 sits just under the report's 3.9-10.2 x10^9/L normal band.
        biomarkerValue: "3.6 ×10⁹/L",
        biomarkerRange: "3.9-10.2",
        biomarkerStatus: "outOfRange",
      },
      {
        photo: blobMediaUrl("/images/questions/blood-winter-blues.jpg"),
        // The report's vitamin D normal band in nmol/L (CH/NL labs).
        biomarkerValue: "45 nmol/L",
        biomarkerRange: "75-150",
        biomarkerStatus: "outOfRange",
        articleSlug:
          "what-your-blood-test-can-reveal-about-the-hidden-swiss-nutrient-gap",
      },
      {
        photo: blobMediaUrl("/images/questions/blood-tired.png"),
        biomarkerValue: "18.9 µmol/L",
        biomarkerRange: "11-28",
        biomarkerStatus: "normal",
      },
    ],
    processSteps: [],
    diseaseCategories: [],
    // Austria launches with the MRI Advanced and Pro packages only
    // (2026-09-28) and sells no standalone test. Whether a scan or panel sits
    // inside one of those two tiers or isn't offered in Austria at all, there
    // is nothing to buy on its own, so the detail page 404s exactly as it does
    // for DE. Restore alongside an Austrian a-la-carte catalogue, not when
    // AT_EXPANSION opens.
    availability: { "de-de": false, "de-en": false, "at-de": false },
  },
  {
    slug: "prevention-starter",
    price: 199,
    duration: "15min",
    heroImage: blobMediaUrl("/images/homepage/service-blood.jpeg"),
    trustBadgeKeys: [
      "trustBadgeQuickSafe",
      "trustBadgeDoctorReviewed",
      "trustBadgeActionableReport",
    ],
    pricingType: "blood",
    // No bookingProductId: booking is not wired for this product yet, so the
    // detail page renders a coming-soon/disabled CTA (see Task 7).
    biomarkerPackages: ["prevention-starter"],
    stepsImages: [
      blobMediaUrl("/images/immunity/immunity-step-book-2.jpg"),
      blobMediaUrl("/images/immunity/immunity-step-blood-draw.jpg"),
      blobMediaUrl("/images/mri/walkthrough/step-report-v2.png"),
    ],
    stepsKeyPrefix: "preventionStarter",
    quickFacts: [
      {
        label: "qfBloodTimeLabel",
        value: "qfBloodTimeValue",
        unit: "qfBloodTimeUnit",
        detail: "qfBloodTimeDetail",
        icon: "clock",
      },
      {
        label: "qfBloodAreasLabel",
        value: "",
        detail: "qfBloodAreasDetail",
        icon: "droplet",
      },
      {
        label: "qfBloodMarkersLabel",
        value: "cms:biomarker-count",
        detail: "qfBloodMarkersDetail",
        icon: "body-scan",
      },
      {
        label: "",
        value: "",
        detail: "qfBloodSummaryDetail",
        icon: "clock",
      },
      {
        label: "qfBloodPrepLabel",
        value: "",
        detail: "qfBloodPrepDetail",
        icon: "checklist",
      },
      {
        label: "qfBloodForWhomLabel",
        value: "18-80",
        detail: "qfBloodForWhomDetail",
        icon: "person",
      },
    ],
    questions: [
      {
        photo: blobMediaUrl("/images/questions/blood-dizzy.png"),
        biomarkerValue: "165 µg/L",
        biomarkerRange: "30-350",
        biomarkerStatus: "normal",
        articleSlug: "iron-deficiency-what-a-full-iron-panel-can-tell-you",
      },
      {
        photo: blobMediaUrl("/images/questions/blood-colds.jpeg"),
        // White blood cells, not a unitless lymphocyte figure (AHD-13587):
        // 3.6 sits just under the report's 3.9-10.2 x10^9/L normal band.
        biomarkerValue: "3.6 ×10⁹/L",
        biomarkerRange: "3.9-10.2",
        biomarkerStatus: "outOfRange",
      },
      {
        photo: blobMediaUrl("/images/questions/blood-winter-blues.jpg"),
        // German reports print vitamin D in µg/L: the same 45 nmol/L reading,
        // against the report's 30-60 µg/L normal band.
        biomarkerValue: "18 µg/L",
        biomarkerRange: "30-60",
        biomarkerStatus: "outOfRange",
        articleSlug:
          "what-your-blood-test-can-reveal-about-the-hidden-swiss-nutrient-gap",
      },
      {
        photo: blobMediaUrl("/images/questions/blood-tired.png"),
        biomarkerValue: "18.9 µmol/L",
        biomarkerRange: "11-28",
        biomarkerStatus: "normal",
      },
    ],
    processSteps: [],
    diseaseCategories: [],
    availability: {
      "ch-en": false,
      "ch-de": false,
      "ch-fr": false,
      "nl-en": false,
      "nl-nl": false,
      "at-de": false,
    },
  },
  {
    slug: "calcium-score",
    // Germany sells the blood check-up and its add-ons, nothing else, so every
    // imaging product is unavailable there: the detail page 404s (the
    // `isAvailable` gate in tests/v/[code]/[slug]/page.tsx), the listing drops
    // the card and app/sitemap.ts stops emitting the URL. Restore when
    // DE_EXPANSION opens MRI booking.
    // Austria launches with the MRI Advanced and Pro packages only
    // (2026-09-28) and sells no standalone test. Whether a scan or panel sits
    // inside one of those two tiers or isn't offered in Austria at all, there
    // is nothing to buy on its own, so the detail page 404s exactly as it does
    // for DE. Restore alongside an Austrian a-la-carte catalogue, not when
    // AT_EXPANSION opens.
    availability: { "de-de": false, "de-en": false, "at-de": false },
    price: 470,
    duration: "10min",
    heroImage: blobMediaUrl("/images/homepage/service-calcium.jpeg"),
    trustBadgeKeys: [
      "trustBadgeQuickSafe",
      "trustBadgeDoctorReviewed",
      "trustBadgeActionableReport",
    ],
    pricingType: "mri",
    stepsImages: [
      blobMediaUrl("/images/immunity/immunity-step-book-2.jpg"),
      // Shares the scanner photo with the MRI-based tests (Tristan's call,
      // 2026-07-29) even though this product is a low-dose CT. Renders as
      // step 3 here — the pre-consultation card sits at step 2.
      blobMediaUrl("/images/steps/mri/02-scan-v2.avif"),
      blobMediaUrl("/images/mri/walkthrough/step-report-v2.png"),
    ],
    stepsKeyPrefix: "calcium",
    hasPreConsultation: true,
    // TODO(AHD-6093): swap to a dedicated pre-consultation photo when one
    // lands in blob. Borrowing the mamma consultation shot in the meantime.
    preConsultImage: blobMediaUrl("/images/steps/mamma/02-consultation.jpg"),
    quickFacts: [
      {
        label: "qfCalciumTimeLabel",
        value: "qfCalciumTimeValue",
        unit: "qfCalciumTimeUnit",
        detail: "qfCalciumTimeDetail",
        icon: "clock",
      },
      {
        label: "qfCalciumImagingLabel",
        value: "",
        detail: "qfCalciumImagingDetail",
        icon: "imaging",
      },
      {
        label: "qfCalciumFocusLabel",
        value: "qfCalciumFocusValue",
        detail: "qfCalciumFocusDetail",
        icon: "body-scan",
      },
      { label: "", value: "", detail: "qfCalciumSummaryDetail", icon: "clock" },
      {
        label: "qfCalciumPrepLabel",
        value: "",
        detail: "qfCalciumPrepDetail",
        icon: "checklist",
      },
      {
        label: "qfCalciumForWhomLabel",
        value: "40-70",
        detail: "qfCalciumForWhomDetail",
        icon: "person",
      },
    ],
    // Any Agatston score above 0 means calcified plaque is present (SCCT:
    // 1–10 = minimal plaque), so only the 0 AU example reads "normal".
    questions: [
      {
        photo: blobMediaUrl("/images/questions/calcium-cholesterol.png"),
        biomarkerValue: "2 AU",
        biomarkerRange: "> 0",
        biomarkerStatus: "observation",
        articleSlug: "coronary-calcium-score-a-guide-to-the-heart-scan-test",
      },
      {
        photo: blobMediaUrl("/images/questions/calcium-diabetes.png"),
        biomarkerValue: "9 AU",
        biomarkerRange: "> 0",
        biomarkerStatus: "observation",
        articleSlug:
          "the-hidden-link-between-insulin-resistance-and-type-2-diabetes",
      },
      {
        photo: blobMediaUrl("/images/questions/calcium-fifty.png"),
        biomarkerValue: "0 AU",
        biomarkerRange: "> 0",
        biomarkerStatus: "normal",
      },
      {
        photo: blobMediaUrl("/images/questions/calcium-family.png"),
        biomarkerValue: "2 AU",
        biomarkerRange: "> 0",
        biomarkerStatus: "observation",
        articleSlug:
          "chronic-inflammation-and-your-heart-the-risk-factor-hiding-in-plain-sight",
      },
    ],
    processSteps: [],
    diseaseCategories: [],
  },
  {
    slug: "body-composition",
    // Germany sells the blood check-up and its add-ons, nothing else, so every
    // imaging product is unavailable there: the detail page 404s (the
    // `isAvailable` gate in tests/v/[code]/[slug]/page.tsx), the listing drops
    // the card and app/sitemap.ts stops emitting the URL. Restore when
    // DE_EXPANSION opens MRI booking.
    // Austria kept this one. It is not separately purchasable there —
    // Austria sells the three MRI packages only — but it IS delivered, as a
    // feature of Advanced ("Körperzusammensetzung" on the package card), so the page
    // explains something an Austrian visitor actually receives. The hero
    // shows no price on an EUR market (`showPrice` is `currency === "CHF"`)
    // and the CTA is the waitlist, so nothing offers a standalone sale.
    availability: { "de-de": false, "de-en": false },
    price: 100,
    memberPrice: 89,
    heroBothPrices: true,
    duration: "durationIncludedMri",
    heroImage: blobMediaUrl("/images/addons/body-composition-hero.jpeg"),
    trustBadgeKeys: [],
    pricingType: "mri",
    biomarkerPackages: ["addon-body"],
    stepsImages: [
      blobMediaUrl("/images/immunity/immunity-step-book-2.jpg"),
      blobMediaUrl("/images/steps/mri/02-scan-v2.avif"),
      blobMediaUrl("/images/mri/walkthrough/step-report-v2.png"),
    ],
    stepsKeyPrefix: "mriAddon",
    quickFacts: [
      {
        label: "qfBodyCompTimeLabel",
        value: "qfBodyCompTimeValue",
        unit: "qfBodyCompTimeUnit",
        detail: "qfBodyCompTimeDetail",
        icon: "clock",
      },
      {
        label: "qfBodyCompRadLabel",
        value: "",
        detail: "qfBodyCompRadDetail",
        icon: "no-radiation",
      },
      {
        label: "qfBodyCompMetricsLabel",
        value: "4+",
        detail: "qfBodyCompMetricsDetail",
        icon: "body-scan",
      },
      {
        label: "",
        value: "",
        detail: "qfBodyCompSummaryDetail",
        icon: "clock",
      },
      {
        label: "qfBodyCompPrepLabel",
        value: "",
        detail: "qfBodyCompPrepDetail",
        icon: "checklist",
      },
      {
        label: "qfBodyCompForWhomLabel",
        value: "qfBodyCompForWhomValue",
        detail: "qfBodyCompForWhomDetail",
        icon: "person",
      },
    ],
    questions: [
      {
        photo: blobMediaUrl("/images/questions/body-fat-muscle.jpeg"),
        biomarkerValue: "5.4 L",
        biomarkerRange: "< 10.1",
        biomarkerStatus: "normal",
      },
      {
        photo: blobMediaUrl("/images/questions/body-diet.jpeg"),
        biomarkerValue: "1.6 L",
        biomarkerRange: "< 9.5",
        biomarkerStatus: "normal",
        articleSlug:
          "visceral-fat-the-hidden-health-risks-of-belly-fat-and-how-to-reduce-it",
      },
      {
        photo: blobMediaUrl("/images/questions/body-metabolism.jpeg"),
        biomarkerValue: "0.4 L/L",
        biomarkerRange: "< 1",
        biomarkerStatus: "normal",
        articleSlug:
          "the-hidden-link-between-insulin-resistance-and-type-2-diabetes",
      },
      {
        photo: blobMediaUrl("/images/questions/body-aging.jpeg"),
        biomarkerValue: "15.4",
        biomarkerRange: "< 35",
        biomarkerStatus: "normal",
      },
    ],
    processSteps: [],
    diseaseCategories: [],
  },
  {
    slug: DEXA_TEST_SLUG,
    // Germany sells the blood check-up and its add-ons, nothing else, so every
    // imaging product is unavailable there: the detail page 404s (the
    // `isAvailable` gate in tests/v/[code]/[slug]/page.tsx), the listing drops
    // the card and app/sitemap.ts stops emitting the URL. Restore when
    // DE_EXPANSION opens MRI booking.
    // Austria launches with the MRI Advanced and Pro packages only
    // (2026-09-28) and sells no standalone test. Whether a scan or panel sits
    // inside one of those two tiers or isn't offered in Austria at all, there
    // is nothing to buy on its own, so the detail page 404s exactly as it does
    // for DE. Restore alongside an Austrian a-la-carte catalogue, not when
    // AT_EXPANSION opens.
    availability: { "de-de": false, "de-en": false, "at-de": false },
    price: 135,
    memberPrice: 119,
    heroBothPrices: true,
    duration: "dexaDuration",
    heroImage: blobMediaUrl("/images/addons/dexa-hero.jpeg"),
    trustBadgeKeys: [],
    pricingType: "mri",
    biomarkerPackages: ["addon-dexa"],
    stepsImages: [
      blobMediaUrl("/images/immunity/immunity-step-book-2.jpg"),
      blobMediaUrl("/images/steps/mri/02-scan-v2.avif"),
      blobMediaUrl("/images/mri/walkthrough/step-report-v2.png"),
    ],
    stepsKeyPrefix: "dexa",
    // 5 stats + 1 description block (slot [3] renders without label/icon and
    // includes a CTA — that's the "description block sits below the stats"
    // surface in Sven's content draft).
    quickFacts: [
      {
        label: "qfDexaTimeLabel",
        value: "qfDexaTimeValue",
        unit: "qfDexaTimeUnit",
        detail: "qfDexaTimeDetail",
        icon: "clock",
      },
      {
        label: "qfDexaImagingLabel",
        value: "",
        detail: "qfDexaImagingDetail",
        icon: "imaging",
      },
      {
        label: "qfDexaFocusLabel",
        value: "qfDexaFocusValue",
        detail: "qfDexaFocusDetail",
        icon: "body-scan",
      },
      {
        label: "",
        value: "",
        detail: "qfDexaSummaryDetail",
        icon: "clock",
      },
      {
        label: "qfDexaPrepLabel",
        value: "",
        detail: "qfDexaPrepDetail",
        icon: "checklist",
      },
      {
        label: "qfDexaForWhomLabel",
        value: "qfDexaForWhomValue",
        detail: "qfDexaForWhomDetail",
        icon: "person",
      },
    ],
    // Hero floating tiles render `questions[0]` (top-right) and `questions[1]`
    // (bottom-left) with biomarkerValue/Range/Status — these become the two
    // T-score tiles in Sven's hero spec. Carousel below uses all 4 entries
    // for the emotional Q&A pairs (translation keys dexaQ1..dexaQ4).
    questions: [
      {
        photo: blobMediaUrl("/images/questions/body-aging.jpeg"),
        biomarkerValue: "−0.6",
        biomarkerRange: "> -1.0",
        biomarkerStatus: "normal",
        articleSlug:
          "dexa-scan-what-bone-density-testing-reveals-and-who-benefits-from-it",
      },
      {
        photo: blobMediaUrl("/images/questions/body-metabolism.jpeg"),
        biomarkerValue: "−1.1",
        biomarkerRange: "-1.0 to -2.5",
        biomarkerStatus: "observation",
      },
      {
        photo: blobMediaUrl("/images/questions/body-fat-muscle.jpeg"),
        biomarkerValue: "−0.9",
        biomarkerRange: "> -1.0",
        biomarkerStatus: "normal",
      },
      {
        photo: blobMediaUrl("/images/questions/body-diet.jpeg"),
        biomarkerValue: "−1.3",
        biomarkerRange: "-1.0 to -2.5",
        biomarkerStatus: "observation",
      },
    ],
    processSteps: [],
    diseaseCategories: [],
  },
  {
    slug: GENDER_HEALTH_TEST_SLUG,
    // Germany sells the blood check-up and its add-ons, nothing else, so every
    // imaging product is unavailable there: the detail page 404s (the
    // `isAvailable` gate in tests/v/[code]/[slug]/page.tsx), the listing drops
    // the card and app/sitemap.ts stops emitting the URL. Restore when
    // DE_EXPANSION opens MRI booking.
    // Austria launches with the MRI Advanced and Pro packages only
    // (2026-09-28) and sells no standalone test. Whether a scan or panel sits
    // inside one of those two tiers or isn't offered in Austria at all, there
    // is nothing to buy on its own, so the detail page 404s exactly as it does
    // for DE. Restore alongside an Austrian a-la-carte catalogue, not when
    // AT_EXPANSION opens.
    availability: { "de-de": false, "de-en": false, "at-de": false },
    price: 150,
    duration: "durationIncludedMri",
    heroImage: blobMediaUrl("/images/addons/gender-health.jpg"),
    trustBadgeKeys: [],
    pricingType: "mri",
    stepsImages: [
      blobMediaUrl("/images/immunity/immunity-step-book-2.jpg"),
      blobMediaUrl("/images/steps/mri/02-scan-v2.avif"),
      blobMediaUrl("/images/mri/walkthrough/step-report-v2.png"),
    ],
    stepsKeyPrefix: "genderHealth",
    quickFacts: [
      {
        label: "qfGenderHealthTimeLabel",
        value: "qfGenderHealthTimeValue",
        detail: "qfGenderHealthTimeDetail",
        icon: "clock",
      },
      {
        label: "qfGenderHealthRadiationLabel",
        value: "",
        detail: "qfGenderHealthRadiationDetail",
        icon: "no-radiation",
      },
      {
        label: "qfGenderHealthFocusLabel",
        value: "qfGenderHealthFocusValue",
        detail: "qfGenderHealthFocusDetail",
        icon: "body-scan",
      },
      {
        label: "",
        value: "",
        detail: "qfGenderHealthSummaryDetail",
        icon: "clock",
      },
      {
        label: "qfGenderHealthPrepLabel",
        value: "",
        detail: "qfGenderHealthPrepDetail",
        icon: "checklist",
      },
      {
        label: "qfGenderHealthForWhomLabel",
        value: "40+",
        detail: "qfGenderHealthForWhomDetail",
        icon: "person",
      },
    ],
    questions: [
      {
        photo: blobMediaUrl("/images/questions/question-aging.png"),
        biomarkerValue: "PI-RADS 2",
        biomarkerRange: "Low suspicion",
        biomarkerStatus: "normal",
        articleSlug: "prostate-cancer-symptoms-screening-and-early-detection",
      },
      {
        photo: blobMediaUrl("/images/questions/question-bloating.png"),
        biomarkerValue: "No findings",
        biomarkerValueKey: "noFindingSingular",
        biomarkerStatus: "negative",
        articleSlug:
          "endometriosis-why-the-silent-disease-is-so-loud-for-1-in-10-women",
      },
      {
        photo: blobMediaUrl("/images/questions/body-diet.jpeg"),
        biomarkerValue: "3.2 cm",
        biomarkerRange: "< 5 cm",
        biomarkerStatus: "normal",
      },
      {
        photo: blobMediaUrl("/images/questions/body-aging.jpeg"),
        biomarkerValue: "Early follow-up",
        biomarkerRange: "If indicated",
        biomarkerStatus: "observation",
      },
    ],
    processSteps: [],
    diseaseCategories: [
      {
        category: "men",
        diseases: [
          {
            name: "Benign prostatic hyperplasia (BPH)",
            slug: "benign-prostatic-hyperplasia",
          },
          {
            name: "Prostatitis",
            slug: "prostatitis",
          },
          {
            name: "Indeterminate prostate lesion",
            slug: "indeterminate-prostate-lesion",
          },
          {
            name: "Prostate cancer > 3 cm",
            slug: "prostate-cancer-3cm",
          },
        ],
      },
      {
        category: "women",
        diseases: [
          {
            name: "Deep infiltrating endometriosis",
            slug: "deep-infiltrating-endometriosis",
          },
          {
            name: "Endometrial cancer",
            slug: "endometrial-cancer",
          },
          {
            name: "Indeterminate ovarian lesion",
            slug: "indeterminate-ovarian-lesion",
          },
          {
            name: "Uterine fibroid",
            slug: "uterine-fibroid",
          },
        ],
      },
    ],
  },
  {
    slug: PROSTATE_MRI_TEST_SLUG,
    // Germany sells the blood check-up and its add-ons, nothing else, so every
    // imaging product is unavailable there: the detail page 404s (the
    // `isAvailable` gate in tests/v/[code]/[slug]/page.tsx), the listing drops
    // the card and app/sitemap.ts stops emitting the URL. Restore when
    // DE_EXPANSION opens MRI booking.
    // Austria launches with the MRI Advanced and Pro packages only
    // (2026-09-28) and sells no standalone test. Whether a scan or panel sits
    // inside one of those two tiers or isn't offered in Austria at all, there
    // is nothing to buy on its own, so the detail page 404s exactly as it does
    // for DE. Restore alongside an Austrian a-la-carte catalogue, not when
    // AT_EXPANSION opens.
    availability: { "de-de": false, "de-en": false, "at-de": false },
    price: 150,
    duration: "durationIncludedMri",
    heroImage: blobMediaUrl("/images/gender-health/prostate-mri-hero.jpg"),
    trustBadgeKeys: [],
    pricingType: "mri",
    stepsImages: [
      blobMediaUrl("/images/steps/mri/01-book.jpg"),
      blobMediaUrl("/images/gender-health/prostate-mri-machine.jpeg"),
      blobMediaUrl("/images/steps/mri/03-results.jpg"),
    ],
    stepsKeyPrefix: "prostateMri",
    // Quick facts render inline in <ProstateMriSection>; the generic grid is
    // skipped for this slug, so the array is intentionally empty.
    quickFacts: [],
    questions: [
      {
        photo: blobMediaUrl("/images/questions/question-aging.png"),
        biomarkerValue: "PI-RADS 2",
        biomarkerRange: "Low suspicion",
        biomarkerStatus: "normal",
      },
      {
        photo: blobMediaUrl("/images/questions/question-bloating.png"),
        biomarkerValue: "No findings",
        biomarkerValueKey: "noFindingSingular",
        biomarkerStatus: "negative",
        hideInHero: true,
      },
      {
        photo: blobMediaUrl("/images/questions/body-diet.jpeg"),
        biomarkerValue: "3.2 cm",
        biomarkerRange: "< 5 cm",
        biomarkerStatus: "normal",
      },
      {
        photo: blobMediaUrl("/images/questions/body-aging.jpeg"),
        biomarkerValue: "Early follow-up",
        biomarkerRange: "If indicated",
        biomarkerStatus: "observation",
      },
    ],
    processSteps: [],
    diseaseCategories: [
      {
        // CMS category slug (its display name is "Men's health") — must match a
        // `categories` slug so the explorer resolves the section description.
        category: "men",
        diseases: [
          {
            name: "Benign prostatic hyperplasia (BPH)",
            slug: "benign-prostatic-hyperplasia",
          },
          {
            name: "Prostatitis",
            slug: "prostatitis",
          },
          {
            name: "Indeterminate prostate lesion",
            slug: "indeterminate-prostate-lesion",
          },
          {
            name: "Prostate cancer > 3 cm",
            slug: "prostate-cancer-3cm",
          },
        ],
      },
    ],
  },
  {
    slug: OVARIES_MRI_TEST_SLUG,
    // Germany sells the blood check-up and its add-ons, nothing else, so every
    // imaging product is unavailable there: the detail page 404s (the
    // `isAvailable` gate in tests/v/[code]/[slug]/page.tsx), the listing drops
    // the card and app/sitemap.ts stops emitting the URL. Restore when
    // DE_EXPANSION opens MRI booking.
    // Austria launches with the MRI Advanced and Pro packages only
    // (2026-09-28) and sells no standalone test. Whether a scan or panel sits
    // inside one of those two tiers or isn't offered in Austria at all, there
    // is nothing to buy on its own, so the detail page 404s exactly as it does
    // for DE. Restore alongside an Austrian a-la-carte catalogue, not when
    // AT_EXPANSION opens.
    availability: { "de-de": false, "de-en": false, "at-de": false },
    price: 150,
    duration: "durationIncludedMri",
    heroImage: blobMediaUrl("/images/gender-health/ovaries-mri-hero.jpg"),
    trustBadgeKeys: [],
    pricingType: "mri",
    stepsImages: [
      blobMediaUrl("/images/steps/mri/01-book.jpg"),
      blobMediaUrl("/images/gender-health/pelvic-mri-machine.jpeg"),
      blobMediaUrl("/images/steps/mri/03-results.jpg"),
    ],
    stepsKeyPrefix: "ovariesMri",
    // Quick facts render inline in <OvariesMriSection>; the generic grid is
    // skipped for this slug. Questions render via the bespoke help section
    // (ovariesMriHelp* keys), so the generic data-driven array is empty.
    quickFacts: [],
    questions: [],
    processSteps: [],
    diseaseCategories: [
      {
        // CMS category slug (its display name is "Women's health") — must match a
        // `categories` slug so the explorer resolves the section description.
        category: "women",
        diseases: [
          {
            name: "Deep infiltrating endometriosis",
            slug: "deep-infiltrating-endometriosis",
          },
          {
            name: "Uterine fibroid",
            slug: "uterine-fibroid",
          },
          {
            name: "Indeterminate ovarian lesion",
            slug: "indeterminate-ovarian-lesion",
          },
          {
            name: "Endometrial cancer",
            slug: "endometrial-cancer",
          },
        ],
      },
    ],
  },
  {
    slug: "brain-volumetry",
    // Germany sells the blood check-up and its add-ons, nothing else, so every
    // imaging product is unavailable there: the detail page 404s (the
    // `isAvailable` gate in tests/v/[code]/[slug]/page.tsx), the listing drops
    // the card and app/sitemap.ts stops emitting the URL. Restore when
    // DE_EXPANSION opens MRI booking.
    // Austria dropped it too (2026-09-30). It had been kept because Pro
    // delivers it ("Hirnvolumetrie" on the package card) and the page
    // explained something an Austrian visitor receives — but a page under
    // /tests/ reads as a thing you can buy, and Austria sells packages only.
    // Body composition stays on that older reasoning for now, so the two are
    // deliberately no longer a pair.
    //
    // WHEN THE QUIZ OPENS FOR AUSTRIA, read this first: lib/quiz/market.ts
    // defines `pro_mri` as full-body MRI PLUS brain volumetry, and resolves a
    // tier to its least-available service. With this slug not available in AT,
    // Pro would resolve to `not_available` and quietly drop out of the Austrian
    // recommendations — for one of the three packages Austria sells. Give AT
    // its own `pro_mri` entry in `tierServicesForCountry` (as DE already has
    // for blood) rather than re-listing this page. Harmless today: page:quiz is
    // ch+nl+de, so nothing reads it for AT yet.
    availability: { "de-de": false, "de-en": false, "at-de": false },
    price: 200,
    duration: "durationIncludedProMri",
    heroImage: blobMediaUrl("/images/addons/brain-volumetry-hero.jpeg"),
    trustBadgeKeys: [],
    pricingType: "mri",
    biomarkerPackages: ["addon-brain"],
    stepsImages: [
      blobMediaUrl("/images/immunity/immunity-step-book-2.jpg"),
      blobMediaUrl("/images/steps/mri/02-scan-v2.avif"),
      blobMediaUrl("/images/mri/walkthrough/step-report-v2.png"),
    ],
    stepsKeyPrefix: "mriAddon",
    quickFacts: [
      {
        label: "qfBrainTimeLabel",
        value: "qfBrainTimeValue",
        unit: "qfBrainTimeUnit",
        detail: "qfBrainTimeDetail",
        icon: "clock",
      },
      {
        label: "qfBrainRadLabel",
        value: "",
        detail: "qfBrainRadDetail",
        icon: "no-radiation",
      },
      {
        label: "qfBrainRegionsLabel",
        value: "",
        detail: "qfBrainRegionsDetail",
        icon: "body-scan",
      },
      { label: "", value: "", detail: "qfBrainSummaryDetail", icon: "clock" },
      {
        label: "qfBrainPrepLabel",
        value: "",
        detail: "qfBrainPrepDetail",
        icon: "checklist",
      },
      {
        label: "qfBrainForWhomLabel",
        value: "45+",
        detail: "qfBrainForWhomDetail",
        icon: "person",
      },
    ],
    questions: [
      {
        photo: blobMediaUrl("/images/questions/brain-memory.jpeg"),
        biomarkerValue: "0.46%",
        biomarkerRange: "0.40–0.50",
        biomarkerStatus: "normal",
        articleSlug: "dementia-and-alzheimer-s-early-detection-and-prevention",
      },
      {
        photo: blobMediaUrl("/images/questions/brain-active.jpeg"),
        biomarkerValue: "1.02%",
        biomarkerRange: "0.88–1.10",
        biomarkerStatus: "normal",
      },
      {
        photo: blobMediaUrl("/images/questions/brain-forgetful.jpeg"),
        biomarkerValue: "34.2",
        biomarkerRange: "32–38",
        biomarkerStatus: "normal",
      },
      {
        photo: blobMediaUrl("/images/questions/brain-aging.jpeg"),
        biomarkerValue: "1.04",
        biomarkerRange: "0.89–1.08",
        biomarkerStatus: "normal",
      },
    ],
    processSteps: [],
    diseaseCategories: [],
  },
  {
    slug: "mamma-mri",
    // Germany sells the blood check-up and its add-ons, nothing else, so every
    // imaging product is unavailable there: the detail page 404s (the
    // `isAvailable` gate in tests/v/[code]/[slug]/page.tsx), the listing drops
    // the card and app/sitemap.ts stops emitting the URL. Restore when
    // DE_EXPANSION opens MRI booking.
    // Austria launches with the MRI Advanced and Pro packages only
    // (2026-09-28) and sells no standalone test. Whether a scan or panel sits
    // inside one of those two tiers or isn't offered in Austria at all, there
    // is nothing to buy on its own, so the detail page 404s exactly as it does
    // for DE. Restore alongside an Austrian a-la-carte catalogue, not when
    // AT_EXPANSION opens.
    availability: { "de-de": false, "de-en": false, "at-de": false },
    price: 800,
    duration: "30min",
    heroImage: blobMediaUrl("/images/addons/mamma-mri-hero.jpg"),
    trustBadgeKeys: [],
    pricingType: "mri",
    stepsImages: [
      blobMediaUrl("/images/immunity/immunity-step-book-2.jpg"),
      blobMediaUrl("/images/steps/mri/02-scan-v2.avif"),
      blobMediaUrl("/images/mri/walkthrough/step-report-v2.png"),
    ],
    stepsKeyPrefix: "mamma",
    hasPreConsultation: true,
    preConsultImage: blobMediaUrl("/images/steps/mamma/02-consultation.jpg"),
    quickFacts: [
      {
        label: "qfMammaTimeLabel",
        value: "qfMammaTimeValue",
        unit: "qfMammaTimeUnit",
        detail: "qfMammaTimeDetail",
        icon: "clock",
      },
      {
        label: "qfMammaRadLabel",
        value: "",
        detail: "qfMammaRadDetail",
        icon: "no-radiation",
      },
      {
        label: "qfMammaFocusLabel",
        value: "qfMammaFocusValue",
        detail: "qfMammaFocusDetail",
        icon: "body-scan",
      },
      { label: "", value: "", detail: "qfMammaSummaryDetail", icon: "clock" },
      {
        label: "qfMammaPrepLabel",
        value: "",
        detail: "qfMammaPrepDetail",
        icon: "checklist",
      },
      {
        label: "qfMammaForWhomLabel",
        value: "qfMammaForWhomValue",
        detail: "qfMammaForWhomDetail",
        icon: "person",
      },
    ],
    questions: [
      {
        photo: blobMediaUrl("/images/questions/mamma-mammogram.jpg"),
        biomarkerValue: "1",
        biomarkerStatus: "normal",
        articleSlug: "mamma-mri-a-deep-look-at-breast-health",
      },
      {
        photo: blobMediaUrl("/images/questions/mamma-implants.jpeg"),
        biomarkerValue: "Intact",
        biomarkerValueKey: "intact",
        biomarkerStatus: "negative",
      },
      {
        photo: blobMediaUrl("/images/questions/mamma-dense-tissue.jpg"),
        biomarkerValue: "99%",
        biomarkerStatus: "normal",
      },
      {
        photo: blobMediaUrl("/images/questions/mamma-comfort.jpg"),
        biomarkerValue: "None",
        biomarkerValueKey: "none",
        biomarkerStatus: "negative",
      },
    ],
    processSteps: [],
    diseaseCategories: [],
  },
  {
    slug: "essential-b-vitamins-test",
    // Austria launches with the MRI Advanced and Pro packages only
    // (2026-09-28) and sells no standalone test. Whether a scan or panel sits
    // inside one of those two tiers or isn't offered in Austria at all, there
    // is nothing to buy on its own, so the detail page 404s exactly as it does
    // for DE. Restore alongside an Austrian a-la-carte catalogue, not when
    // AT_EXPANSION opens.
    availability: { "at-de": false },
    price: 49,
    duration: "15min",
    heroImage: blobMediaUrl("/images/addons/b-vitamins.jpeg"),
    trustBadgeKeys: [],
    pricingType: "blood",
    biomarkerPackages: ["addon-bvitamins"],
    stepsImages: [
      blobMediaUrl("/images/immunity/immunity-step-book-2.jpg"),
      blobMediaUrl("/images/immunity/immunity-step-blood-draw.jpg"),
      blobMediaUrl("/images/mri/walkthrough/step-report-v2.png"),
    ],
    stepsKeyPrefix: "blood",
    quickFacts: [
      {
        label: "qfBVitaminTimeLabel",
        value: "qfBVitaminTimeValue",
        unit: "qfBVitaminTimeUnit",
        detail: "qfBVitaminTimeDetail",
        icon: "clock",
      },
      {
        label: "qfBVitaminAreasLabel",
        value: "",
        detail: "qfBVitaminAreasDetail",
        icon: "droplet",
      },
      {
        label: "qfBVitaminMarkersLabel",
        value: "cms:biomarker-count",
        detail: "qfBVitaminMarkersDetail",
        icon: "body-scan",
      },
      {
        label: "",
        value: "",
        detail: "qfBVitaminSummaryDetail",
        icon: "clock",
      },
      {
        label: "qfBVitaminPrepLabel",
        value: "",
        detail: "qfBVitaminPrepDetail",
        icon: "checklist",
      },
      {
        label: "qfBVitaminForWhomLabel",
        value: "18-80",
        detail: "qfBVitaminForWhomDetail",
        icon: "person",
      },
    ],
    questions: [
      {
        photo: blobMediaUrl("/images/questions/bvitamin-focus.jpeg"),
        biomarkerValue: "12 nmol/L",
        biomarkerRange: "> 7",
        biomarkerStatus: "normal",
        articleSlug:
          "vitamin-b12-deficiency-testing-holotranscobalamin-homocysteine",
      },
      {
        photo: blobMediaUrl("/images/questions/bvitamin-mood.jpg"),
        biomarkerValue: "260 pmol/L",
        biomarkerRange: "> 221",
        biomarkerStatus: "normal",
      },
      {
        photo: blobMediaUrl("/images/questions/bvitamin-skin.jpeg"),
        biomarkerValue: "9.3 nmol/L",
        biomarkerRange: "> 7",
        biomarkerStatus: "normal",
      },
      {
        photo: blobMediaUrl("/images/questions/blood-tired.png"),
        biomarkerValue: "340 pmol/L",
        biomarkerRange: "> 221",
        biomarkerStatus: "normal",
      },
    ],
    processSteps: [],
    diseaseCategories: [],
  },
  {
    slug: "omega-blood-test",
    // Austria launches with the MRI Advanced and Pro packages only
    // (2026-09-28) and sells no standalone test. Whether a scan or panel sits
    // inside one of those two tiers or isn't offered in Austria at all, there
    // is nothing to buy on its own, so the detail page 404s exactly as it does
    // for DE. Restore alongside an Austrian a-la-carte catalogue, not when
    // AT_EXPANSION opens.
    availability: { "at-de": false },
    price: 129,
    memberPrice: 119,
    duration: "15min",
    heroImage: blobMediaUrl("/images/homepage/service-omega.jpeg"),
    trustBadgeKeys: [],
    pricingType: "blood",
    biomarkerPackages: ["addon-omega"],
    stepsImages: [
      blobMediaUrl("/images/immunity/immunity-step-book-2.jpg"),
      blobMediaUrl("/images/immunity/immunity-step-blood-draw.jpg"),
      blobMediaUrl("/images/mri/walkthrough/step-report-v2.png"),
    ],
    stepsKeyPrefix: "blood",
    quickFacts: [
      {
        label: "qfOmegaTimeLabel",
        value: "qfOmegaTimeValue",
        unit: "qfOmegaTimeUnit",
        detail: "qfOmegaTimeDetail",
        icon: "clock",
      },
      {
        label: "qfOmegaAreasLabel",
        value: "",
        detail: "qfOmegaAreasDetail",
        icon: "droplet",
      },
      {
        label: "qfOmegaMarkersLabel",
        value: "cms:biomarker-count",
        detail: "qfOmegaMarkersDetail",
        icon: "body-scan",
      },
      { label: "", value: "", detail: "qfOmegaSummaryDetail", icon: "clock" },
      {
        label: "qfOmegaPrepLabel",
        value: "",
        detail: "qfOmegaPrepDetail",
        icon: "checklist",
      },
      {
        label: "qfOmegaForWhomLabel",
        value: "18-80",
        detail: "qfOmegaForWhomDetail",
        icon: "person",
      },
    ],
    questions: [
      {
        photo: blobMediaUrl("/images/questions/omega-supplement.jpg"),
        biomarkerValue: "22 mg/L",
        biomarkerRange: "3–36",
        biomarkerStatus: "normal",
      },
      {
        photo: blobMediaUrl("/images/questions/omega-nutrients.jpg"),
        biomarkerValue: "8.7%",
        biomarkerRange: "≥ 8",
        biomarkerStatus: "normal",
        articleSlug:
          "what-your-blood-test-can-reveal-about-the-hidden-swiss-nutrient-gap",
      },
      {
        photo: blobMediaUrl("/images/questions/omega-skin.jpg"),
        biomarkerValue: "240 mg/L",
        biomarkerRange: "180-250",
        biomarkerStatus: "normal",
      },
      {
        photo: blobMediaUrl("/images/questions/omega-joints.jpg"),
        biomarkerValue: "8",
        biomarkerStatus: "normal",
      },
    ],
    processSteps: [],
    diseaseCategories: [],
  },
  {
    slug: "hormone-blood-test-women",
    // Austria launches with the MRI Advanced and Pro packages only
    // (2026-09-28) and sells no standalone test. Whether a scan or panel sits
    // inside one of those two tiers or isn't offered in Austria at all, there
    // is nothing to buy on its own, so the detail page 404s exactly as it does
    // for DE. Restore alongside an Austrian a-la-carte catalogue, not when
    // AT_EXPANSION opens.
    availability: { "at-de": false },
    audience: "female",
    price: 149,
    memberPrice: 119,
    duration: "15min",
    heroImage: blobMediaUrl("/images/addons/hormone-test-women.jpeg"),
    trustBadgeKeys: [],
    pricingType: "blood",
    biomarkerPackages: ["addon-hormones-f"],
    stepsImages: [
      blobMediaUrl("/images/immunity/immunity-step-book-2.jpg"),
      blobMediaUrl("/images/immunity/immunity-step-blood-draw.jpg"),
      blobMediaUrl("/images/mri/walkthrough/step-report-v2.png"),
    ],
    stepsKeyPrefix: "blood",
    quickFacts: [
      {
        label: "qfHormoneWTimeLabel",
        value: "qfHormoneWTimeValue",
        unit: "qfHormoneWTimeUnit",
        detail: "qfHormoneWTimeDetail",
        icon: "clock",
      },
      {
        label: "qfHormoneWAreasLabel",
        value: "",
        detail: "qfHormoneWAreasDetail",
        icon: "droplet",
      },
      {
        label: "qfHormoneWMarkersLabel",
        value: "cms:biomarker-count",
        detail: "qfHormoneWMarkersDetail",
        icon: "body-scan",
      },
      {
        label: "",
        value: "",
        detail: "qfHormoneWSummaryDetail",
        icon: "clock",
      },
      {
        label: "qfHormoneWPrepLabel",
        value: "",
        detail: "qfHormoneWPrepDetail",
        icon: "checklist",
      },
      {
        label: "qfHormoneWForWhomLabel",
        value: "18-80",
        detail: "qfHormoneWForWhomDetail",
        icon: "person",
      },
    ],
    questions: [
      {
        photo: blobMediaUrl("/images/questions/hormone-w-stress.jpg"),
        biomarkerValue: "2.9 µmol/L",
        biomarkerRange: "1.2-9.5",
        biomarkerStatus: "normal",
      },
      {
        photo: blobMediaUrl("/images/questions/hormone-w-mood.jpg"),
        biomarkerValue: "270 pmol/L",
        biomarkerRange: "70-1600",
        biomarkerStatus: "normal",
        articleSlug: "hormone-testing-track-energy-sleep-mood-more",
      },
      {
        photo: blobMediaUrl("/images/questions/hormone-w-training.jpg"),
        biomarkerValue: "3.6",
        biomarkerRange: "0.5-6",
        biomarkerStatus: "normal",
      },
      {
        photo: blobMediaUrl("/images/questions/hormone-w-menopause.jpg"),
        biomarkerValue: "9.8 U/L",
        biomarkerStatus: "normal",
        articleSlug: "perimenopause-blood-test-symptoms-30s-40s",
      },
    ],
    processSteps: [],
    diseaseCategories: [],
  },
  {
    slug: "hormone-blood-test-men",
    // Austria launches with the MRI Advanced and Pro packages only
    // (2026-09-28) and sells no standalone test. Whether a scan or panel sits
    // inside one of those two tiers or isn't offered in Austria at all, there
    // is nothing to buy on its own, so the detail page 404s exactly as it does
    // for DE. Restore alongside an Austrian a-la-carte catalogue, not when
    // AT_EXPANSION opens.
    availability: { "at-de": false },
    audience: "male",
    price: 149,
    memberPrice: 119, // mirrors the women's panel (same base)
    duration: "15min",
    heroImage: blobMediaUrl("/images/addons/hormone-test-men.jpeg"),
    trustBadgeKeys: [],
    pricingType: "blood",
    biomarkerPackages: ["addon-hormones-m"],
    stepsImages: [
      blobMediaUrl("/images/immunity/immunity-step-book-2.jpg"),
      blobMediaUrl("/images/immunity/immunity-step-blood-draw.jpg"),
      blobMediaUrl("/images/mri/walkthrough/step-report-v2.png"),
    ],
    stepsKeyPrefix: "blood",
    questions: [
      {
        photo: blobMediaUrl("/images/questions/hormone-m-stress.jpeg"),
        biomarkerValue: "2.8 µmol/L",
        biomarkerRange: "1.2-9.5",
        biomarkerStatus: "normal",
      },
      {
        photo: blobMediaUrl("/images/questions/hormone-m-training.jpeg"),
        biomarkerValue: "0.38 nmol/L",
        biomarkerRange: "0.2-0.62",
        biomarkerStatus: "normal",
      },
      {
        photo: blobMediaUrl("/images/questions/hormone-m-estrogen.jpeg"),
        biomarkerValue: "95 pmol/L",
        biomarkerRange: "50-160",
        biomarkerStatus: "normal",
      },
      {
        photo: blobMediaUrl("/images/questions/hormone-m-energy.jpg"),
        biomarkerValue: "18.6 nmol/L",
        biomarkerRange: "8-30",
        biomarkerStatus: "normal",
        articleSlug: "hormone-testing-track-energy-sleep-mood-more",
      },
    ],
    processSteps: [],
    diseaseCategories: [],
    quickFacts: [
      {
        label: "qfHormoneMTimeLabel",
        value: "qfHormoneMTimeValue",
        unit: "qfHormoneMTimeUnit",
        detail: "qfHormoneMTimeDetail",
        icon: "clock",
      },
      {
        label: "qfHormoneMAreasLabel",
        value: "",
        detail: "qfHormoneMAreasDetail",
        icon: "droplet",
      },
      {
        label: "qfHormoneMMarkersLabel",
        value: "cms:biomarker-count",
        detail: "qfHormoneMMarkersDetail",
        icon: "body-scan",
      },
      {
        label: "",
        value: "",
        detail: "qfHormoneMSummaryDetail",
        icon: "clock",
      },
      {
        label: "qfHormoneMPrepLabel",
        value: "",
        detail: "qfHormoneMPrepDetail",
        icon: "checklist",
      },
      {
        label: "qfHormoneMForWhomLabel",
        value: "18-80",
        detail: "qfHormoneMForWhomDetail",
        icon: "person",
      },
    ],
  },
  {
    slug: "advanced-blood-panel",
    // Austria launches with the MRI Advanced and Pro packages only
    // (2026-09-28) and sells no standalone test. Whether a scan or panel sits
    // inside one of those two tiers or isn't offered in Austria at all, there
    // is nothing to buy on its own, so the detail page 404s exactly as it does
    // for DE. Restore alongside an Austrian a-la-carte catalogue, not when
    // AT_EXPANSION opens.
    availability: { "at-de": false },
    price: 299,
    duration: "15min",
    heroImage: blobMediaUrl("/images/homepage/service-blood-cells.jpeg"),
    trustBadgeKeys: [],
    pricingType: "blood",
    bookingProductId: "bloodTest",
    quickFacts: [],
    stepsImages: [
      blobMediaUrl("/images/immunity/immunity-step-book-2.jpg"),
      blobMediaUrl("/images/steps/mri/02-scan-v2.avif"),
      blobMediaUrl("/images/mri/walkthrough/step-report-v2.png"),
    ],
    questions: [],
    processSteps: [],
    diseaseCategories: [],
  },
]

export function resolveTestSlug(slug: string): string {
  return TEST_SLUG_ALIASES[slug] ?? slug
}

export function getTestBySlug(slug: string): TestData | undefined {
  return TESTS.find((t) => t.slug === resolveTestSlug(slug))
}

export function getVisibleTests(includeNewTestsPages = false): TestData[] {
  return TESTS.filter((t) => {
    if (TEST_LISTING_HIDDEN_SLUGS.includes(t.slug as never)) return false
    if (!includeNewTestsPages && NEW_TESTS_PAGE_SLUGS.includes(t.slug as never))
      return false
    return true
  })
}
